import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import {
  eingabeAusGramm,
  formatiereKg,
  gewichtEingabe,
  gewichtsVorschau,
  positionAusGewicht,
  type GewichtTaste,
  type WiegeAnfrage,
} from '../gewichtsverkauf';
import { formatiereEuro, type Position } from '../warenkorb';
import { istTastaturEsc } from './Bildschirmtastatur';
import { useFokusfalleAmFenster } from './zahlung-v2/fokus';

/** Tastenfeld wie der Ziffernblock (Klasse `ziffern-tasten`), mit Komma statt „00“. */
const GEWICHT_TASTEN: readonly GewichtTaste[] = [
  '7',
  '8',
  '9',
  '4',
  '5',
  '6',
  '1',
  '2',
  '3',
  ',',
  '0',
  'loeschen',
];

/**
 * Eingabe aus dem Feld (Hardware-/System-Tastatur, Einfügen): nur, was der Ziffernblock auch
 * erlaubt; Punkt gilt als Komma. Das Feld bleibt ein normales Eingabefeld (keine eigene
 * Tastenverarbeitung), damit die Scanerkennung einen Scan darin wie in jedem gesperrten Feld
 * verwerfen und den Wert zurücksetzen kann (H4).
 */
function feldwert(roh: string): string | null {
  const t = roh.trim().replace('.', ',');
  return /^\d{0,2}(,\d{0,3})?$/.test(t) ? t : null;
}

/** Smartphone (GW-S): nur der Dialog-Ziffernblock – kein Autofokus, der die System-Tastatur öffnet. */
function istSchmal(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 600px)').matches;
}

/**
 * Gewichtsdialog für Wiegeartikel nach dem freigegebenen Entwurf GW v1 (Figma `393:19147`,
 * abgeleitet von D8): Kopf mit Bezeichnung und „Wiegeartikel · kg“, Gewicht in kg
 * (Touch-Ziffernblock mit Komma oder Hardwaretastatur), Live-Gesamtpreis, Abbrechen (Esc) und
 * „Zum Bon hinzufügen“ (Enter). Die Rechnung liegt vollständig in `gewichtsverkauf.ts`.
 */
export function Gewichtsdialog(props: {
  anfrage: WiegeAnfrage;
  onHinzufuegen: (p: Position) => void;
  onAbbrechen: () => void;
}) {
  const { anfrage, onHinzufuegen, onAbbrechen } = props;
  const k = anfrage.kachel;
  const [text, setText] = useState(() =>
    anfrage.vorgabe === undefined ? '' : eingabeAusGramm(anfrage.vorgabe.gramm),
  );
  const fenster = useRef<HTMLDivElement | null>(null);
  const feld = useRef<HTMLInputElement | null>(null);
  // Ein Doppeltipp auf „Zum Bon hinzufügen“ darf nur eine Position erzeugen
  const fertig = useRef(false);
  useFokusfalleAmFenster(fenster, true);
  useEffect(() => {
    if (istSchmal()) {
      fenster.current?.focus();
    } else {
      feld.current?.focus();
    }
  }, []);

  const vorschau = gewichtsVorschau(text, k.bruttoCent);
  // Steuersatz wie in der späteren Position (außer Haus ggf. abweichend)
  const steuersatz = positionAusGewicht(k, 1, anfrage.ausserHaus).steuersatz;
  const kilopreis = `${formatiereEuro(k.bruttoCent)} €/kg`;

  function uebernehmen(): void {
    if (vorschau === null || fertig.current) {
      return;
    }
    fertig.current = true;
    onHinzufuegen(positionAusGewicht(k, vorschau.gramm, anfrage.ausserHaus));
  }

  function taste(t: GewichtTaste): void {
    setText((alt) => gewichtEingabe(alt, t));
  }

  function beiTaste(e: ReactKeyboardEvent<HTMLDivElement>): void {
    if (e.altKey || e.ctrlKey || e.metaKey) {
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      // R5 (BT v3): ein Esc, das nur die Bildschirmtastatur geschlossen hat, schließt nichts
      if (!istTastaturEsc(e)) {
        onAbbrechen();
      }
      return;
    }
    if (e.key === 'Enter') {
      // Enter auf „Abbrechen“ bleibt Abbrechen (Klick); sonst übernimmt Enter das Gewicht
      if (e.target instanceof HTMLButtonElement) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      uebernehmen();
      return;
    }
  }

  return (
    <div
      className="zahlung-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gewicht-titel"
      ref={fenster}
      tabIndex={-1}
      onKeyDown={beiTaste}
    >
      <div className="karte dlg gewicht-dialog">
        <div className="dlg-kopf">
          <h2 id="gewicht-titel">{k.bezeichnung}</h2>
          <span className="dlg-meta">
            Wiegeartikel · kg
            {anfrage.vorgabe !== undefined &&
              ` · Gewicht von ${anfrage.vorgabe.herkunft === 'waage' ? 'der Waage' : 'Barcode'}`}
          </span>
        </div>
        <label className="gewicht-feld">
          <span className="dlg-feldname">
            Gewicht in kg · Kilopreis {kilopreis} · USt {steuersatz} %
          </span>
          <span className="ziffern-anzeige gewicht-anzeige">
            <input
              ref={feld}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              aria-describedby="gewicht-summe"
              aria-invalid={text !== '' && vorschau === null}
              // H4: ein Scan wird nie zum Gewicht
              data-scansperre="Gewichtsfeld"
              placeholder="0,000"
              value={text}
              onChange={(e) => {
                const neu = feldwert(e.target.value);
                if (neu !== null) {
                  setText(neu);
                }
              }}
            />
            <span aria-hidden="true">kg</span>
          </span>
        </label>
        <div className="ziffern-tasten" role="group" aria-label="Gewicht eingeben">
          {GEWICHT_TASTEN.map((t) => (
            <button
              key={t}
              type="button"
              // Fokus bleibt im Feld, damit Enter weiter das Gewicht übernimmt
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                taste(t);
              }}
              aria-label={t === 'loeschen' ? 'Letzte Ziffer löschen' : t === ',' ? 'Komma' : t}
            >
              {t === 'loeschen' ? '⌫' : t}
            </button>
          ))}
        </div>
        <div className="gewicht-gesamt">
          <span className="dlg-feldname">
            Gesamtpreis
            {vorschau !== null && ` (${formatiereKg(vorschau.gramm)} × ${kilopreis})`}
          </span>
          <div
            className={vorschau === null && text !== '' ? 'betrag ungueltig' : 'betrag'}
            id="gewicht-summe"
            aria-live="polite"
          >
            {vorschau === null
              ? text === ''
                ? 'Gewicht eingeben'
                : 'Ungültiges Gewicht (0,001 bis 99,999 kg)'
              : `${formatiereEuro(vorschau.bruttoCent)} €`}
          </div>
        </div>
        <div className="dlg-fuss gewicht-fuss">
          <button type="button" className="taste zweit" data-kuerzel="Esc" onClick={onAbbrechen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={vorschau === null}
            onClick={uebernehmen}
          >
            Zum Bon hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}
